import { isNotBlank, isArrayWithValues } from '@bizzdesign/bizzdesign-sdk';
import {
  type FilterItem,
  type ResultSuggestionItem,
  type SearchService,
  type SearchSuggestionGroup,
  type SmartSuggestionItem,
} from '@bizzdesign/data-access-search';
import type {
  AdvancedFilter,
  AuthenticatedUser,
} from '@bizzdesign/shared/models';
import { type Observable, map, of } from 'rxjs';

export type ResultSuggestionsResponse = {
  items: ResultSuggestionItem[];
  totalCount: number;
};

export function getSearchSuggestions(
  searchTerm: string,
  searchService: SearchService,
  advancedFilter: AdvancedFilter | undefined,
): Observable<ResultSuggestionsResponse> {
  if (isNotBlank(searchTerm)) {
    return searchService.searchSuggestions(searchTerm, advancedFilter);
  }
  return of({ items: [], totalCount: 0 });
}

export function getFilterSuggestions(
  searchTerm: string,
  authenticatedUser: AuthenticatedUser | undefined,
  searchService: SearchService,
): Observable<FilterItem[]> {
  if (authenticatedUser) {
    return searchService.searchFilterSuggestions(searchTerm, authenticatedUser);
  }
  return of([]);
}

export function getSmartSearchSuggestionGroup(
  items: SmartSuggestionItem[],
): SearchSuggestionGroup[] {
  return isArrayWithValues(items)
    ? [
        {
          labelKey: 'smartSuggestions.smartSearchLabel',
          tScope: 'featureSearch',
          items,
        },
      ]
    : [];
}

export function getCombinedSuggestions(
  searchTerm: string,
  appliedFilters: FilterItem[],
  resultSuggestions: ResultSuggestionsResponse | null,
  authenticatedUser: AuthenticatedUser | undefined,
  searchService: SearchService,
): Observable<SearchSuggestionGroup[]> {
  return getFilterSuggestions(
    searchTerm,
    authenticatedUser,
    searchService,
  ).pipe(
    map((filterSuggestions) => {
      const combinedSuggestions: SearchSuggestionGroup[] = [];

      if (filterSuggestions.length > 0 || appliedFilters.length > 0) {
        combinedSuggestions.push({
          labelKey: 'suggestions.filter',
          tScope: 'featureSearch',
          items: [
            {
              filters: filterSuggestions.filter(
                (filterSuggestion) =>
                  !appliedFilters
                    .map(
                      (selectedSuggestionFilter) => selectedSuggestionFilter.id,
                    )
                    .includes(filterSuggestion.id),
              ),
              appliedFilters: appliedFilters,
              type: 'filter',
            },
          ],
        });
      }

      if (isNotBlank(searchTerm)) {
        if (resultSuggestions === null) {
          combinedSuggestions.push({
            labelKey: 'suggestions.results',
            tScope: 'featureSearch',
            items: [],
            loading: true,
          });
        } else {
          combinedSuggestions.push({
            labelKey: 'suggestions.resultsWithCount',
            labelParams: { count: String(resultSuggestions.totalCount) },
            tScope: 'featureSearch',
            items: resultSuggestions.items.slice(0, 4),
            loading: false,
          });
        }
      }
      return combinedSuggestions;
    }),
  );
}
